// Types espelhando os DTOs públicos do oma-service (snake_case 1:1 com JSON tags Go).
// Fontes: internal/usecase/calculator/public.go + checkout.go + domain/repository/calculator_repos.go.

export type Audience = 'participante' | 'organizacao';
export type Domain = 'eventos' | 'geral' | 'logistica' | 'agro';
export type Mode = 'calc_only' | 'branded';
export type PurchaseMode = 'lead_only' | 'lead_and_purchase';

export interface InstanceOfferingDetail {
  offering_id: string;
  display_order: number;
  vintage_id: string;
  vintage_year: number;
  vintage_status: string;
  price_brl_per_tco2e: number;
  available_tco2e: number;
  currency: string;
  project_id: string;
  project_slug: string;
  project_name: string;
  project_short_description?: string;
  project_category: string;
  /** Label humanizado PT-BR/EN/ES do project_category. Vem do backend via translator. */
  project_category_label?: string;
  project_cover_image_url?: string;
}

export interface SchemaResponse {
  instance_id: string;
  audience: Audience;
  domain: Domain;
  mode: Mode;
  purchase_mode: PurchaseMode;
  locale_default: string;
  lead_capture_mode: string;
  theme_image_url?: string;
  theme_text_overrides?: Record<string, string>;
  formula_slug: string;
  band_thresholds: Record<string, number>;
  compensation_offers?: InstanceOfferingDetail[];
  /** Cidade onde o evento acontece (domain=eventos). Widget mostra como destino. */
  event_city?: string;
  /** ISO 3166-1 alpha-2; default BR. */
  event_city_country?: string;
}

/** Resposta do GET /v1/public/calculators/{token}/distance?origin=...&mode=... */
export interface DistanceResponse {
  origin_city: string;
  destination_city: string;
  distance_km: number;
  provider: string;
  /** Como o backend obteve o valor — ajusta o helper text do campo.
   * Opcional: o widget pode subir antes do backend (skew de deploy). */
  method?: 'route' | 'haversine_adjusted' | 'great_circle';
}

/** Sugestão de cidade do autocomplete (origem). Espelha o shape do admin. */
export interface CitySuggestion {
  name: string;
  region?: string;
  country?: string;
  display: string;
}

export interface SearchCitiesResponse {
  suggestions: CitySuggestion[];
}

// ── Inputs do formulário (eventos-participante-v1.0.0) ─────────────────
// Espelha eventos.Inputs em internal/usecase/calculator/formulas/eventos/compute.go
export type TransportMode = 'aviao' | 'carro' | 'onibus' | 'metro_trem' | 'bike' | 'a_pe';
export type CarFuel = 'gasolina' | 'etanol' | 'diesel' | 'eletrico';
export type LodgingType =
  | 'hotel'
  | 'hostel'
  | 'airbnb'
  | 'casa_amigos'
  | 'proprio'
  | 'nao_me_hospedei';
export type IntraCityMode = 'a_pe' | 'bicicleta' | 'uber_taxi' | 'metro_onibus' | 'carro_proprio';

export interface EventosInputs {
  // Passo 1
  transport_mode: TransportMode;
  origin_city?: string;
  distance_km: number;
  round_trip: boolean;
  car_fuel?: CarFuel;
  // Passo 2
  event_days: number;
  lodging_nights: number;
  lodging_type: LodgingType;
  intra_city_transport: IntraCityMode;
  intra_city_km_per_day: number;
  intra_city_car_fuel?: CarFuel;
}

export interface SubmitterInput {
  full_name: string;
  email: string;
  consent: boolean;
}

// ── Inputs do formulário (eventos-organizacao-v1.0.0) ───────────────────
// Espelha eventos_org.Inputs em
// internal/usecase/calculator/formulas/eventos_org/compute.go.
//
// Persona: organizador / empresa do evento (não participante). 4 etapas
// com dados + 1 de resultado (port literal mockup 20).
export type FuelUnit = 'BRL' | 'km' | 'L' | 'trips' | 'kWh';

/**
 * Fontes de eletricidade quando o evento arca com a despesa (mockup legacy
 * IBIOMA, Etapa 1, bloco Eletricidade). Espelha `ElectricitySource` Go
 * em internal/usecase/calculator/formulas/eventos_org.
 */
export type ElectricitySource = 'grid' | 'generator' | 'both';

export interface FuelInput {
  value: number;
  unit: FuelUnit | '';
}

export interface SupplierLine {
  name?: string;
  distance_km: number;
}

export interface EventosOrgInputs {
  // ── Etapa 1: Equipe e público + eletricidade ──────────────────────
  crew_count: number;
  venue_capacity: number;
  audience_estimated: number;
  electricity_paid: boolean;
  // Subcampos só relevantes quando electricity_paid=true. Source vazio
  // sinaliza "ainda não respondeu" — backend cai no fallback de estimativa.
  electricity_source?: ElectricitySource;
  /** kWh (direto) ou BRL (convertido server-side via conversion::brl_per_kwh). */
  grid_consumption: FuelInput;
  /** Litros (direto) ou BRL (convertido server-side via brl_per_liter_diesel). */
  generator_fuel: FuelInput;

  // ── Etapa 2: Deslocamento ─────────────────────────────────────────
  internal_fuel: FuelInput;
  employee_private_fuel: FuelInput;
  employee_public_fuel: FuelInput;

  consider_audience: boolean;
  audience_avg_km: number;
  pct_public: number;
  pct_private: number;
  pct_walking: number;
  pct_short_flight: number;
  pct_medium_flight: number;
  pct_long_flight: number;

  consider_suppliers: boolean;
  suppliers: SupplierLine[];

  // ── Etapa 3: Outros ───────────────────────────────────────────────
  duration_hours: number;
  glp_kg: number;
  waste_organic_kg: number;
  waste_recyclable_kg: number;
}

/**
 * Metadata do evento — coletada na Etapa 4 (Identificação) e enviada via
 * `submitter` no compute. Não entra no cálculo; é lead capture.
 */
export interface EventosOrgSubmitterMeta {
  event_name?: string;
  segment?: string;
  cpf_cnpj?: string;
  state?: string;
  city?: string;
  contact_name: string;
  contact_phone?: string;
  contact_email: string;
}

export interface ComputeRequest {
  // Inputs são tipados union: backend desserializa o map<string,any> baseado
  // em (audience, domain). Cada package compute parseia o que conhece e
  // ignora campos extras.
  inputs: EventosInputs | EventosOrgInputs;
  submitter?: SubmitterInput;
  locale?: string;
}

export interface CategoryBreakdown {
  category: string;
  /** Label humanizado PT-BR/EN/ES — vem pronto do backend via translator. */
  label?: string;
  tco2e: number;
}

export interface ComputeResponse {
  result_id: string;
  total_tco2e: number;
  band: string;
  /** Label humanizado da band (ex: "Emissão muito baixa") — vem pronto do backend. */
  band_label?: string;
  formula_slug: string;
  scope_1: number;
  scope_2: number;
  scope_3: number;
  categories: CategoryBreakdown[];
  compensation_offers?: InstanceOfferingDetail[];
  deduped: boolean;
}

/** Snapshot do carbonômetro — agregado público da calc pro painel TV. */
export interface CarbonometerSnapshot {
  instance_id: string;
  public_token: string;
  audience: Audience;
  domain: Domain;
  mode: Mode;
  theme_image_url?: string;
  event_city?: string;
  label?: string;
  period_start: string;
  period_end: string;
  total_tco2e: number;
  submission_count: number;
  event_days_elapsed: number;
  top_categories: CarbonometerCategoryPct[];
}

export interface CarbonometerCategoryPct {
  category: string;
  label: string;
  tco2e: number;
  pct: number;
}

/** Bundle público de i18n pro widget. GET /v1/public/i18n/embed?locale=… */
export interface EmbedI18nBundle {
  locale: string;
  keys: Record<string, string>;
}

export interface CheckoutRequest {
  result_id: string;
  vintage_id: string;
  tco2e_to_buy?: number;
  customer_email: string;
  customer_name?: string;
  return_url: string;
  cancel_url?: string;
  locale?: string;
}

export interface CheckoutResponse {
  payment_order_id: string;
  provider: string;
  session_url: string;
  amount_brl: number;
  tco2e_to_buy: number;
  price_per_tco2e_brl: number;
  expires_at: string;
}

/**
 * PublicPaymentStatus é o shape retornado por GET /v1/public/payments/{order_id}/status,
 * usado pelo polling do iframe checkout. Sem PII.
 */
export interface PublicPaymentStatus {
  order_id: string;
  status: 'pending' | 'paid' | 'failed' | 'expired' | 'cancelled';
  tco2e: number;
  amount_brl: number;
}

// ── Envelope ────────────────────────────────────────────────────────
// oma-commons/router/response.Envelope: { success, message?, data?, error?, meta? }
export interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: ApiError;
}

export interface ApiError {
  code: string;
  message: string;
  locale?: string;
  details?: Record<string, unknown>;
}

export class ApiCallError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiCallError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}
